import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { cx } from './lib/tv';
import { usePortalContainer, type PortalContainer } from './lib/portal';

export interface TooltipProps {
  /** Tooltip text / content. Nothing renders when empty. */
  content: React.ReactNode;
  /** The trigger — a single element that accepts a ref and DOM props (Button, IconButton, `<span tabIndex={0}>`…). */
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** Distance from the trigger in CSS px. Default 6. */
  sideOffset?: number;
  /** Hover delay in ms. Default 400. */
  delay?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** Portal target (scaled stage). Defaults to the nearest `PortalContainerProvider`, else `<body>`. */
  container?: PortalContainer;
  className?: string;
}

/**
 * Plain text tooltip — not drawn in the product; uses `glass-tooltip` r8, padding 6/10, 400 12/16
 * (spec §6.10, §9 Q14). Opens on hover and keyboard focus.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  delay = 400,
  open,
  onOpenChange,
  disabled,
  container,
  className,
}: TooltipProps) {
  const portalContainer = usePortalContainer(container);
  if (content == null || content === '' || content === false) return children;
  return (
    <BaseTooltip.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined} disabled={disabled}>
      <BaseTooltip.Trigger delay={delay} render={children} />
      <BaseTooltip.Portal container={portalContainer}>
        <BaseTooltip.Positioner side={side} align={align} sideOffset={sideOffset} className="z-90">
          <BaseTooltip.Popup
            className={cx(
              'glass-tooltip max-w-280 origin-(--transform-origin) rounded-8 px-10 py-6 text-xs text-fg shadow-float',
              'transition-[opacity,scale] duration-150 ease-standard data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0',
              className,
            )}
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

/** Shares the hover delay between neighbouring tooltips (toolbars, rails). */
export function TooltipProvider({ children, delay = 400, closeDelay }: { children: React.ReactNode; delay?: number; closeDelay?: number }) {
  return (
    <BaseTooltip.Provider delay={delay} closeDelay={closeDelay}>
      {children}
    </BaseTooltip.Provider>
  );
}
